import {
  Box,
  Divider,
  Flex,
  Heading,
  Link,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { FeatureFlag, PagePaths, PageTitles } from '@revelio/core';
import { Button } from '@revelio/ui';

import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import DashboardPage from '../DashboardPage';
import { redeemDataBuilderShare } from './api/data-builder-api';
import type { DataBuilderPayload } from './api/data-builder-api.types';
import { extractDataBuilderErrorMessage } from './api/data-builder-error-message';
import { withDataBuilderQueryPolicy } from './api/data-builder-request-policies';
import { hydrateStoreFromPayload } from './api/hydrate-store-from-payload';
import { DataBuilderPageTemplate } from './components/ui/DataBuilderPageTemplate';
import { DataBuilderConfigSummary } from './DataBuilderConfigSummary';
import { isDeliveryDownloadable } from './DataBuilderStatus';
import { dataBuilderStore } from './data-builder.store';
import { DataBuilderPaths } from './steps/data-builder-steps';

export const DataBuilderShareRedeem = () => {
  const navigate = useNavigate();
  const { shareToken } = useParams<{ shareToken: string }>();
  const shareLinksEnabled = useUnleashFlag(FeatureFlag.DataBuilderShareLinks);

  const {
    data: share,
    isLoading,
    error,
    isError,
  } = useQuery(
    withDataBuilderQueryPolicy({
      queryKey: ['data-builder', 'share-redeem', shareToken],
      queryFn: () => redeemDataBuilderShare({ token: shareToken ?? '' }),
      enabled: shareLinksEnabled,
      retry: false,
    })
  );

  // Feature off — this route should not be reachable; send users to the
  // deliverables status page.
  if (!shareLinksEnabled) {
    return <Navigate to={`/${PagePaths.DATA_BUILDER_STATUS}`} replace />;
  }

  const downloadUrl =
    share && isDeliveryDownloadable('FINISHED', share.url ?? null)
      ? share.url
      : null;

  const handleAccept = () => {
    if (!share) return;
    const payload = share.payload as DataBuilderPayload;
    const hydratedState = hydrateStoreFromPayload(payload);
    dataBuilderStore.getState().reset();
    dataBuilderStore.setState({
      ...hydratedState,
      loadedSavedDatasetId: null,
    });
    navigate(`/${PagePaths.DATA_BUILDER}/${DataBuilderPaths.DATASET}`);
  };

  return (
    <DashboardPage
      title={[PageTitles.DELIVERABLES, 'Shared Delivery']}
      loading={isLoading}
    >
      {isError && (
        <Box
          bg="white"
          rounded="lg"
          p={6}
          data-testid="data-builder-share-redeem-error"
        >
          <Heading fontSize="16px" fontWeight="600" mb={2} color="text.primary">
            Invalid Share Link
          </Heading>
          <Text fontSize="sm" mb={4} color="text.primary">
            {extractDataBuilderErrorMessage(
              error,
              'This share link is invalid or has expired.'
            )}
          </Text>
          <Button
            color="primary"
            size="sm"
            onClick={() => navigate(`/${PagePaths.DATA_BUILDER_STATUS}`)}
          >
            Back to deliveries
          </Button>
        </Box>
      )}

      {!isError && share && (
        <DataBuilderPageTemplate>
          <Flex
            direction="column"
            height="100%"
            overflow="hidden"
            data-testid="data-builder-share-redeem"
          >
            <Box flexShrink={0} p={6} pb={4}>
              <Heading fontSize="18px" fontWeight="600" color="text.primary">
                {share.client} - {share.name}
              </Heading>
              <Text fontSize="sm" color="text.primary" mt={1}>
                Review the configuration below, then accept it to edit and run
                your own copy.
                {downloadUrl && (
                  <>
                    {' '}
                    You can also{' '}
                    <Link
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="text.primary"
                      textDecoration="underline"
                      data-testid="data-builder-share-redeem-download"
                    >
                      download data
                    </Link>
                    .
                  </>
                )}
              </Text>
            </Box>

            <Divider flexShrink={0} />

            <Box flex={1} minHeight={0} overflow="auto" px={6} py={4}>
              <DataBuilderConfigSummary
                payload={share.payload as DataBuilderPayload}
                status="FINISHED"
                url={share.url ?? null}
              />
            </Box>

            <Flex
              as="footer"
              align="center"
              justify="flex-end"
              gap={3}
              flexShrink={0}
              borderTopWidth="1px"
              borderTopStyle="solid"
              borderColor="gray.200"
              bg="white"
              px={6}
              py={4}
            >
              <Button
                color="primary"
                size="sm"
                onClick={handleAccept}
                data-testid="data-builder-share-redeem-accept"
              >
                Accept and Edit
              </Button>
            </Flex>
          </Flex>
        </DataBuilderPageTemplate>
      )}

      {!isError && !share && !isLoading && (
        <Box bg="white" rounded="lg" p={6}>
          <Spinner size="sm" />
        </Box>
      )}
    </DashboardPage>
  );
};

export default DataBuilderShareRedeem;
